// One day of the daily site log (V0.4.15) — phone first. Quick answers, a voice note SWI AI turns into the summary,
// and per active building: photos, works done (ticked from the PBS look-ahead), a short summary and any hold-up.
// Signing off locks the day (the Super Admin can reopen it).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { thumbUrl, write } from '../lib/api';
import { uploadFile } from '../lib/files';
import { fmtDateTime } from '../lib/format';
import { lookaheadFor, norm, type WeeklyRead } from '../lib/weekly';
import { ACTIVE_STAGES, addDays, dayLabel, fijiToday, matchOrgs, tickWorks, WEATHER, worksList, type DailyLog, type DayBuilding, type VoiceProposal, type Work } from '../lib/daily';
import { Button, Drawer, Empty, errText, useToast } from '../ui/kit';
import { C3Busy, C3PageLoading } from '../ui/C3Loader';
import { useViewer } from '../ui/Viewer';
import { OfflineNote, useConnection } from '../lib/connection';
import { VoiceNote, type VoiceResult } from '../ui/daily/VoiceNote';

type Bld = { id: string; name: string; short_code: string | null; stage: string | null; sort: number | null };
type Org = { id: string; name: string; short_code: string | null; kind: string | null };
type Photo = { id: string; linked_id: string; original_name: string; mime: string | null; created_at: string };
const NONE = '00000000-0000-0000-0000-000000000000';
const VISITOR_KINDS = new Set(['architect', 'engineer', 'consultant', 'client']);
const ONSITE_KINDS = new Set(['contractor', 'supplier']);

function Thumb({ p, onOpen }: { p: Photo; onOpen: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    thumbUrl(p.id).then((r) => live && setUrl(r.url)).catch(() => {});
    return () => {
      live = false;
    };
  }, [p.id]);
  return (
    <button type="button" className="dl-pic" onClick={onOpen} aria-label={`Open photo ${p.original_name}`} style={url ? { backgroundImage: `url("${url}")` } : undefined}>
      {url ? null : <span className="muted small">photo</span>}
    </button>
  );
}

export default function DailyDay() {
  const { date: param } = useParams();
  const date = /^\d{4}-\d{2}-\d{2}$/.test(param ?? '') ? param! : fijiToday();
  const { siteId, can, access } = useApp();
  const toast = useToast();
  const nav = useNavigate();
  const viewer = useViewer();
  const conn = useConnection();
  const sid = siteId ?? NONE;
  const editable = can('daily_log', 4);

  const logs = useRows<DailyLog>(() => supabase.from('daily_logs').select('*').eq('site_id', sid).eq('log_date', date).is('deleted_at', null), [sid, date], ['daily_logs']);
  const log = logs.rows[0] ?? null;
  const dbs = useRows<DayBuilding>(() => supabase.from('daily_log_buildings').select('*').eq('log_id', log?.id ?? NONE).is('deleted_at', null), [log?.id], ['daily_log_buildings']);
  const photos = useRows<Photo>(
    () => supabase.from('files').select('id, linked_id, original_name, mime, created_at').eq('linked_table', 'daily_log_buildings').in('linked_id', dbs.rows.length ? dbs.rows.map((d) => d.id) : [NONE]).is('deleted_at', null).order('created_at'),
    [dbs.rows.map((d) => d.id).join()],
    ['files'],
  );
  const blds = useRows<Bld>(() => supabase.from('buildings').select('id, name, short_code, stage, sort').eq('site_id', sid).is('deleted_at', null).order('sort', { nullsFirst: false }).order('name'), [sid]);
  const orgs = useRows<Org>(() => supabase.from('organisations').select('id, name, short_code, kind').is('deleted_at', null).order('name'), []);
  const [la, setLa] = useState<WeeklyRead | null>(null);
  useEffect(() => {
    void supabase.rpc('c3_lookahead', { p_site: sid }).then(({ data }) => setLa((data as WeeklyRead | null) ? ({ ...(data as WeeklyRead), registers: [], notes: null } as WeeklyRead) : null));
  }, [sid]);
  const [showAll, setShowAll] = useState(false);
  const [voice, setVoice] = useState(false);
  const [review, setReview] = useState<VoiceResult | null>(null);
  const [busy, setBusy] = useState('');

  const signed = log?.status === 'signed';
  const locked = !editable || (signed && !access?.super_admin) || conn.status === 'offline';
  const shown = useMemo(() => {
    const withRow = new Set(dbs.rows.map((d) => d.building_id));
    return blds.rows.filter((b) => showAll || ACTIVE_STAGES.has(b.stage ?? '') || withRow.has(b.id));
  }, [blds.rows, dbs.rows, showAll]);
  const visitorOrgs = orgs.rows.filter((o) => VISITOR_KINDS.has(o.kind ?? ''));
  const onsiteOrgs = orgs.rows.filter((o) => ONSITE_KINDS.has(o.kind ?? ''));

  async function ensureLog(): Promise<DailyLog> {
    if (log) return log;
    const row = await write<DailyLog>('daily_logs', 'insert', { values: { site_id: sid, log_date: date }, summary: `Started the daily log for ${date}` });
    await logs.reload();
    return row;
  }
  async function setLog(values: Record<string, unknown>, what: string, aiKeep = false) {
    try {
      const l = await ensureLog();
      const ai = aiKeep ? l.ai_fields : (l.ai_fields ?? []).filter((k) => !Object.keys(values).includes(k));
      await write('daily_logs', 'update', { id: l.id, values: { ...values, ai_fields: ai }, summary: `Daily log ${date}: ${what}` });
      await logs.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  async function ensureRow(b: Bld): Promise<DayBuilding> {
    const have = dbs.rows.find((d) => d.building_id === b.id);
    if (have) return have;
    const l = await ensureLog();
    const row = await write<DayBuilding>('daily_log_buildings', 'insert', { values: { log_id: l.id, building_id: b.id }, summary: `Daily log ${date}: ${b.name}` });
    await dbs.reload();
    return row;
  }
  async function setRow(b: Bld, values: Record<string, unknown>, what: string) {
    try {
      const r = await ensureRow(b);
      await write('daily_log_buildings', 'update', { id: r.id, values, summary: `Daily log ${date} · ${b.name}: ${what}` });
      await dbs.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }
  async function addPhotos(b: Bld, files: FileList | null) {
    if (!files?.length) return;
    setBusy(`ph-${b.id}`);
    try {
      const r = await ensureRow(b);
      for (const f of [...files].filter((x) => /^image\//.test(x.type)))
        await uploadFile(f, { siteId: sid, bucket: 'photos', linkedTable: 'daily_log_buildings', linkedId: r.id, title: `${b.name} · ${date}` });
      await photos.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy('');
    }
  }
  const lookahead = (b: Bld) => lookaheadFor(la, b.name);

  if (logs.loading || blds.loading) return <C3PageLoading label="Loading the daily log" />;

  return (
    <div className="dl-page">
      <div className="small"><Link to={`/daily?m=${date.slice(0, 7)}`}>Daily site log</Link> › {date.slice(0, 7)}</div>
      <div className="dl-head">
        <div className="wk-row">
          <Button className="dl-sq" aria-label="Previous day" onClick={() => nav(`/daily/${addDays(date, -1)}`)}>‹</Button>
          <div>
            <h1>{dayLabel(date)}</h1>
            <div className="wk-row small muted">
              {log ? <span className={`wk-badge ${signed ? 'ok' : 'warn'}`}>{signed ? 'Signed off' : 'Draft'}</span> : <span className="wk-badge mute">Not started</span>}
              {signed ? <span>by {log?.signed_name ?? 'SWI'} · {fmtDateTime(log?.signed_at ?? null)}</span> : log ? <span>last saved {fmtDateTime(log.updated_at)}</span> : null}
            </div>
          </div>
          <Button className="dl-sq" aria-label="Next day" disabled={date >= fijiToday()} onClick={() => nav(`/daily/${addDays(date, 1)}`)}>›</Button>
        </div>
        <div className="wk-row">
          <OfflineNote />
          {editable && signed && access?.super_admin ? <Button onClick={() => void setLog({ status: 'draft' }, 'reopened (Super Admin)', true)}>Reopen the day</Button> : null}
          {editable && log && !signed ? (
            <Button kind="primary" disabled={conn.status === 'offline'} onClick={() => void setLog({ status: 'signed' }, 'signed off', true)}>Sign off the day</Button>
          ) : null}
        </div>
      </div>

      {editable && !locked ? (
        <button type="button" className="dl-voicebtn" onClick={() => setVoice(true)}>
          <span className="dl-micsm" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></svg></span>
          <span><b>Talk me through the day</b><span className="muted small">SWI AI writes the summary and answers the questions below</span></span>
        </button>
      ) : null}

      {(log?.summary || (log?.voice ?? []).length) ? (
        <section className="dl-ai">
          <div className="wk-row" style={{ justifyContent: 'space-between' }}>
            <h2><span className="wk-badge ai">SWI AI</span> Day summary</h2>
            <span className="muted small">{(log?.voice ?? []).length} voice note{(log?.voice ?? []).length === 1 ? '' : 's'}</span>
          </div>
          <SummaryBox value={log?.summary ?? ''} ai={(log?.ai_fields ?? []).includes('summary')} disabled={locked} onSave={(v) => void setLog({ summary: v || null }, 'summary edited')} />
          {(log?.voice ?? []).map((v, i) => (
            <details key={i} className="small">
              <summary>Voice note {i + 1} · {Math.floor(v.seconds / 60)}:{String(v.seconds % 60).padStart(2, '0')}{v.file_id ? ' · recording kept' : ''}</summary>
              <p className="muted">{v.transcript}</p>
              {v.file_id ? <button type="button" className="linkbtn small" onClick={() => viewer({ id: v.file_id!, name: `Voice note ${i + 1}`, mime: 'audio/webm' })}>Play / download the recording</button> : null}
            </details>
          ))}
        </section>
      ) : null}

      <section className="dl-card" aria-label="Quick questions">
        <h2>Quick questions</h2>
        <Q label="Weather" ai={(log?.ai_fields ?? []).includes('weather')}>
          {WEATHER.map((w) => (
            <Chip key={w.value} on={log?.weather === w.value} disabled={locked} onClick={() => void setLog({ weather: log?.weather === w.value ? null : w.value }, `weather ${w.label}`)}>{w.label}</Chip>
          ))}
        </Q>
        <Q label="Work stopped for weather?" ai={(log?.ai_fields ?? []).includes('weather_stop_h')}>
          <Chip on={log?.weather_stop_h === 0} disabled={locked} onClick={() => void setLog({ weather_stop_h: 0 }, 'no weather stop')}>No</Chip>
          <label className="dl-hours">Yes ·
            <input type="number" min={0} max={24} step={0.5} inputMode="decimal" disabled={locked} aria-label="Hours stopped for weather" defaultValue={log?.weather_stop_h ? String(log.weather_stop_h) : ''} key={`h${log?.weather_stop_h}`} onBlur={(e) => { const v = e.target.value === '' ? null : Number(e.target.value); if (v !== (log?.weather_stop_h ?? null)) void setLog({ weather_stop_h: v }, `weather stop ${v ?? '—'} h`); }} /> h
          </label>
        </Q>
        <Q label="Barge today?" ai={(log?.ai_fields ?? []).includes('barge')}>
          <Chip on={log?.barge === false} disabled={locked} onClick={() => void setLog({ barge: false, barge_note: null }, 'no barge')}>No</Chip>
          <Chip on={log?.barge === true} disabled={locked} onClick={() => void setLog({ barge: true }, 'barge came')}>Yes</Chip>
          {log?.barge ? <input className="dl-note" disabled={locked} aria-label="What came on the barge" placeholder="What came / when" defaultValue={log.barge_note ?? ''} key={`b${log.barge_note}`} onBlur={(e) => e.target.value.trim() !== (log.barge_note ?? '') && void setLog({ barge_note: e.target.value.trim() || null }, 'barge note')} /> : null}
        </Q>
        <Q label="Staff boat transfers" ai={(log?.ai_fields ?? []).includes('transfers')}>
          <div className="dl-step">
            <button type="button" className="dl-sbtn" aria-label="One less transfer" disabled={locked || !(log?.transfers ?? 0)} onClick={() => void setLog({ transfers: Math.max(0, (log?.transfers ?? 0) - 1) }, 'transfers')}>−</button>
            <b>{log?.transfers ?? 0}</b>
            <button type="button" className="dl-sbtn" aria-label="One more transfer" disabled={locked} onClick={() => void setLog({ transfers: (log?.transfers ?? 0) + 1 }, 'transfers')}>+</button>
          </div>
        </Q>
        <Q label="Consultants / client on site" ai={(log?.ai_fields ?? []).includes('visitor_org_ids')}>
          {visitorOrgs.map((o) => {
            const on = (log?.visitor_org_ids ?? []).includes(o.id);
            return <Chip key={o.id} on={on} disabled={locked} onClick={() => void setLog({ visitor_org_ids: on ? log!.visitor_org_ids.filter((x) => x !== o.id) : [...(log?.visitor_org_ids ?? []), o.id] }, `${o.name} ${on ? 'removed' : 'on site'}`)}>{o.short_code || o.name}</Chip>;
          })}
          {!visitorOrgs.length ? <span className="muted small">Add consultants under Organisations &amp; contacts</span> : null}
        </Q>
        <Q label="Contractors &amp; subcontractors on site" ai={(log?.ai_fields ?? []).includes('onsite_org_ids')}>
          {onsiteOrgs.map((o) => {
            const on = (log?.onsite_org_ids ?? []).includes(o.id);
            return <Chip key={o.id} on={on} disabled={locked} onClick={() => void setLog({ onsite_org_ids: on ? log!.onsite_org_ids.filter((x) => x !== o.id) : [...(log?.onsite_org_ids ?? []), o.id] }, `${o.name} ${on ? 'removed' : 'on site'}`)}>{o.short_code || o.name}</Chip>;
          })}
        </Q>
        {(
          [
            ['hold_up', 'Any hold-up today?'],
            ['incident', 'Incident or near miss?'],
            ['instructions', 'Site instruction given?'],
          ] as const
        ).map(([k, l]) => (
          <Q key={k} label={l} ai={(log?.ai_fields ?? []).includes(k)}>
            <Chip on={log?.[k] === false} disabled={locked} onClick={() => void setLog({ [k]: false }, `${l} no`)}>No</Chip>
            <Chip on={log?.[k] === true} disabled={locked} onClick={() => void setLog({ [k]: true }, `${l} yes`)}>Yes</Chip>
            {k === 'instructions' && log?.instructions ? <Link className="small" to="/r/rfis">Record it under RFIs &amp; site instructions</Link> : null}
          </Q>
        ))}
      </section>

      <h2 className="dl-h">Active buildings — photos &amp; works done</h2>
      {!shown.length ? <Empty>No active buildings. <button type="button" className="linkbtn" onClick={() => setShowAll(true)}>Show all buildings</button></Empty> : null}
      {shown.map((b) => {
        const row = dbs.rows.find((d) => d.building_id === b.id) ?? null;
        const list = worksList(lookahead(b), row?.works ?? []);
        const pics = photos.rows.filter((p) => p.linked_id === row?.id);
        const input = `ph-${b.id}`;
        return (
          <section key={b.id} className={`dl-card ${row?.no_work ? 'nowork' : ''}`} aria-label={b.name}>
            <div className="wk-row" style={{ justifyContent: 'space-between' }}>
              <h2>{b.name}</h2>
              <span className="wk-row small muted">
                <span>{pics.length} photo{pics.length === 1 ? '' : 's'}</span>
                <Chip on={Boolean(row?.no_work)} disabled={locked} onClick={() => void setRow(b, { no_work: !row?.no_work }, row?.no_work ? 'work today' : 'no work today')}>No work today</Chip>
              </span>
            </div>
            {!row?.no_work ? (
              <>
                <div className="dl-photos">
                  {!locked ? (
                    <label className="dl-shoot">
                      <input id={input} type="file" accept="image/*" capture="environment" multiple hidden onChange={(e) => void addPhotos(b, e.target.files)} aria-label={`Take a photo for ${b.name}`} />
                      {busy === input ? <C3Busy text="Uploading…" accent /> : <>＋ Take photo</>}
                    </label>
                  ) : null}
                  {pics.map((p) => <Thumb key={p.id} p={p} onOpen={() => viewer({ id: p.id, name: p.original_name, mime: p.mime }, pics.map((x) => ({ id: x.id, name: x.original_name, mime: x.mime })))} />)}
                </div>
                <div className="muted small">Works done today{lookahead(b).length ? ` — from the PBS look-ahead${la?.week_no ? ` (week ${la.week_no})` : ''}` : ''}</div>
                <div className="dl-ticks">
                  {list.map((w, i) => (
                    <label key={i} className="check">
                      <input type="checkbox" checked={w.done} disabled={locked} onChange={(e) => void setRow(b, { works: list.map((x, k) => (k === i ? { ...x, done: e.target.checked } : x)).filter((x) => x.done || x.from === 'own') }, `${w.text} ${e.target.checked ? 'done' : 'not done'}`)} /> {w.text}
                    </label>
                  ))}
                  {!locked ? <AddWork onAdd={(t) => void setRow(b, { works: [...list.filter((x) => x.done || x.from === 'own'), { text: t, done: true, from: 'own' } as Work] }, `added ${t}`)} /> : null}
                </div>
                <SummaryBox label="Summary" value={row?.summary ?? ''} disabled={locked} placeholder="A line or two — or let the voice note write it" onSave={(v) => void setRow(b, { summary: v || null }, 'summary')} />
                <div className="dl-hold">
                  <input className="dl-note" disabled={locked} aria-label={`Hold-up at ${b.name}`} placeholder="Any hold-up here? (e.g. waiting on layout approval)" defaultValue={row?.hold_up ?? ''} key={`hu${row?.hold_up}`} onBlur={(e) => e.target.value.trim() !== (row?.hold_up ?? '') && void setRow(b, { hold_up: e.target.value.trim() || null }, 'hold-up')} />
                  {row?.hold_up ? (
                    <label className="check small"><input type="checkbox" checked={row.eot_evidence} disabled={locked} onChange={(e) => void setRow(b, { eot_evidence: e.target.checked }, e.target.checked ? 'kept as delay evidence' : 'not delay evidence')} /> Keep as delay / EOT evidence</label>
                  ) : null}
                </div>
              </>
            ) : null}
          </section>
        );
      })}
      {!showAll ? <button type="button" className="linkbtn small" onClick={() => setShowAll(true)}>Show the other buildings</button> : null}

      <VoiceNote
        open={voice}
        onClose={() => setVoice(false)}
        context={{ site_id: sid, log_date: date, buildings: shown.map((b) => ({ name: b.name, works: lookahead(b) })), orgs: [...visitorOrgs, ...onsiteOrgs].map((o) => o.name) }}
        onResult={(r) => {
          setVoice(false);
          setReview(r);
        }}
      />
      {review ? (
        <VoiceReview
          r={review}
          blds={shown}
          orgs={orgs.rows}
          onClose={() => setReview(null)}
          onKeep={async (keep) => {
            try {
              const l = await ensureLog();
              let fileId: string | null = null;
              if (review.audio && review.audio.size > 0) {
                const ext = /mp4/.test(review.audio.type) ? 'm4a' : 'webm';
                const f = new File([review.audio], `Voice note ${date}.${ext}`, { type: review.audio.type || 'audio/webm' });
                const up = await uploadFile(f, { siteId: sid, bucket: 'documents', linkedTable: 'daily_logs', linkedId: l.id, title: `Voice note ${date}` });
                fileId = (up.row as { id: string }).id;
              }
              await write('daily_logs', 'update', {
                id: l.id,
                values: { ...keep.log, ai_fields: [...new Set([...(l.ai_fields ?? []), ...Object.keys(keep.log)])], voice: [...(l.voice ?? []), { file_id: fileId, seconds: review.seconds, transcript: review.transcript.slice(0, 20000), at: new Date().toISOString() }] },
                summary: `Daily log ${date}: SWI AI filled ${Object.keys(keep.log).length} answers from a voice note (kept by the PM)`,
              });
              for (const kb of keep.buildings) {
                const b = shown.find((x) => x.id === kb.id)!;
                const row = await ensureRow(b);
                const list = worksList(lookahead(b), row.works ?? []);
                await write('daily_log_buildings', 'update', {
                  id: row.id,
                  values: { ...(kb.summary !== undefined ? { summary: kb.summary } : {}), ...(kb.hold_up ? { hold_up: kb.hold_up } : {}), ...(kb.no_work !== undefined ? { no_work: kb.no_work } : {}), works: tickWorks(list, kb.works).filter((x) => x.done || x.from === 'own') },
                  summary: `Daily log ${date} · ${b.name}: filled from a voice note`,
                });
              }
              setReview(null);
              await Promise.all([logs.reload(), dbs.reload()]);
              toast({ tone: 'ok', text: 'Kept — check the purple answers' });
            } catch (e) {
              toast({ tone: 'error', text: errText(e) });
            }
          }}
        />
      ) : null}
    </div>
  );
}

function Q({ label, ai, children }: { label: string; ai?: boolean; children: React.ReactNode }) {
  return (
    <div className={`dl-q ${ai ? 'ai' : ''}`} role="group" aria-label={label}>
      <span className="dl-ql">{label}{ai ? <span className="wk-badge ai">SWI AI</span> : null}</span>
      <div className="dl-opts">{children}</div>
    </div>
  );
}
function Chip({ on, disabled, onClick, children }: { on?: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" className={`dl-o ${on ? 'on' : ''}`} aria-pressed={Boolean(on)} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  );
}
function SummaryBox({ value, onSave, disabled, ai, label, placeholder }: { value: string; onSave: (v: string) => void; disabled?: boolean; ai?: boolean; label?: string; placeholder?: string }) {
  const [v, setV] = useState(value);
  const last = useRef(value);
  useEffect(() => {
    setV(value);
    last.current = value;
  }, [value]);
  return (
    <label className={`field dl-sum ${ai ? 'ai' : ''}`}>
      {label ? <span className="field-label">{label}</span> : null}
      <textarea rows={3} value={v} disabled={disabled} placeholder={placeholder} aria-label={label ?? 'Day summary'} onChange={(e) => setV(e.target.value)} onBlur={() => v.trim() !== last.current.trim() && onSave(v.trim())} />
    </label>
  );
}
function AddWork({ onAdd }: { onAdd: (t: string) => void }) {
  const [t, setT] = useState('');
  return (
    <input
      className="dl-note"
      value={t}
      placeholder="+ Something else (Enter)"
      aria-label="Add work done"
      onChange={(e) => setT(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && t.trim()) {
          onAdd(t.trim());
          setT('');
        }
      }}
    />
  );
}

type Keep = { log: Record<string, unknown>; buildings: { id: string; summary?: string | null; hold_up?: string | null; no_work?: boolean; works: string[] }[] };
function VoiceReview({ r, blds, orgs, onClose, onKeep }: { r: VoiceResult; blds: Bld[]; orgs: Org[]; onClose: () => void; onKeep: (k: Keep) => Promise<void> }) {
  const p: VoiceProposal = r.proposal;
  const W: Record<string, string> = { fine: 'Fine', cloud: 'Cloud', showers: 'Showers', heavy_rain: 'Heavy rain' };
  const name = (ids: string[]) => ids.map((id) => orgs.find((o) => o.id === id)?.name ?? '').filter(Boolean).join(', ');
  const visitors = matchOrgs(p.visitors, orgs);
  const onsite = matchOrgs(p.onsite, orgs);
  const lines = [
    p.weather ? { k: 'weather', label: `Weather: ${W[p.weather]}`, v: { weather: p.weather } } : null,
    p.weather_stop_h !== null ? { k: 'weather_stop_h', label: `Work stopped for weather: ${p.weather_stop_h ? `${p.weather_stop_h} h` : 'no'}`, v: { weather_stop_h: p.weather_stop_h } } : null,
    p.barge !== null ? { k: 'barge', label: `Barge: ${p.barge ? `yes${p.barge_note ? ` — ${p.barge_note}` : ''}` : 'no'}`, v: { barge: p.barge, barge_note: p.barge ? p.barge_note : null } } : null,
    p.transfers !== null ? { k: 'transfers', label: `Staff boat transfers: ${p.transfers}`, v: { transfers: p.transfers } } : null,
    visitors.length ? { k: 'visitor_org_ids', label: `Consultants on site: ${name(visitors)}`, v: { visitor_org_ids: visitors } } : null,
    onsite.length ? { k: 'onsite_org_ids', label: `Contractors on site: ${name(onsite)}`, v: { onsite_org_ids: onsite } } : null,
    p.hold_up !== null ? { k: 'hold_up', label: `Hold-up today: ${p.hold_up ? 'yes' : 'no'}`, v: { hold_up: p.hold_up } } : null,
    p.incident !== null ? { k: 'incident', label: `Incident or near miss: ${p.incident ? 'yes' : 'no'}`, v: { incident: p.incident } } : null,
    p.instructions !== null ? { k: 'instructions', label: `Site instruction given: ${p.instructions ? 'yes' : 'no'}`, v: { instructions: p.instructions } } : null,
    p.summary ? { k: 'summary', label: `Day summary: ${p.summary}`, v: { summary: p.summary } } : null,
  ].filter(Boolean) as { k: string; label: string; v: Record<string, unknown> }[];
  const bl = p.buildings
    .map((x) => ({ x, b: blds.find((b) => norm(b.name) === norm(x.name)) ?? blds.find((b) => norm(b.name).includes(norm(x.name)) || norm(x.name).includes(norm(b.name))) }))
    .filter((y): y is { x: VoiceProposal['buildings'][number]; b: Bld } => Boolean(y.b));
  const [off, setOff] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const toggle = (k: string) => setOff((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const count = lines.filter((l) => !off.has(l.k)).length + bl.filter((y) => !off.has(`b:${y.b.id}`)).length;
  return (
    <Drawer open title="SWI AI filled in — check and keep" onClose={onClose}>
      <div className="dl-review">
        {!lines.length && !bl.length ? <Empty>SWI AI couldn't pick out any answers — add them by hand, or record again with more detail.</Empty> : null}
        {lines.map((l) => (
          <label key={l.k} className="dl-rv">
            <input type="checkbox" checked={!off.has(l.k)} onChange={() => toggle(l.k)} />
            <span><b>{l.label}</b>{p.quotes[l.k] ? <span className="dl-quote">“{p.quotes[l.k]}”</span> : null}</span>
          </label>
        ))}
        {bl.map(({ x, b }) => (
          <label key={b.id} className="dl-rv">
            <input type="checkbox" checked={!off.has(`b:${b.id}`)} onChange={() => toggle(`b:${b.id}`)} />
            <span>
              <b>{b.name}{x.no_work ? ' — no work today' : `${x.works_done.length ? ` — ${x.works_done.length} works ticked` : ''}${x.summary ? ' + summary' : ''}`}</b>
              {x.summary ? <span className="dl-quote">{x.summary}</span> : null}
              {x.works_done.length ? <span className="muted small">{x.works_done.join(' · ')}</span> : null}
              {x.hold_up ? <span className="muted small">Hold-up: {x.hold_up}</span> : null}
            </span>
          </label>
        ))}
        <p className="muted small">The recording and what was heard stay on the day, so anyone can check what was said.</p>
        <div className="wk-row wk-end">
          <Button onClick={onClose}>Discard</Button>
          <Button
            kind="primary"
            disabled={!count || busy}
            onClick={() => {
              setBusy(true);
              void onKeep({
                log: Object.assign({}, ...lines.filter((l) => !off.has(l.k)).map((l) => l.v)),
                buildings: bl.filter((y) => !off.has(`b:${y.b.id}`)).map(({ x, b }) => ({ id: b.id, summary: x.summary ?? undefined, hold_up: x.hold_up, no_work: x.no_work ?? undefined, works: x.works_done })),
              }).finally(() => setBusy(false));
            }}
          >
            {busy ? <C3Busy text="Keeping…" accent /> : `Keep ${count}`}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
